import { useCallback, useMemo } from "react";
import { SectionList, type StyleProp, type ViewStyle } from "react-native";
import Box from "../Box";
import ChallengeListItem from "../ChallengeListItem";
import { CustomListSubheader } from "../CustomListSubheader";
import EmptyState from "../EmptyView";

type EmptyStateConfig = {
	title: string;
	subtitle: string;
	cta: string;
};

type GetEmptyStateParams = {
	hiddenChallenges: number;
	activeFilterCount: number;
	hasSearchQuery: boolean;
	isPremiumSelected?: boolean;
	isCompletedSelected?: boolean;
};

const getEmptyStateConfig = ({
	hiddenChallenges,
	activeFilterCount,
	hasSearchQuery,
	isPremiumSelected,
	isCompletedSelected,
}: GetEmptyStateParams): EmptyStateConfig => {
	if (hasSearchQuery) {
		return {
			title: `${hiddenChallenges} challenges hidden by search`,
			subtitle: "Try different keywords or clear your search",
			cta: "Clear search",
		};
	}

	if (activeFilterCount > 0) {
		const activeFilters = [
			isPremiumSelected && "premium",
			isCompletedSelected && "completed",
		].filter(Boolean);

		return {
			title: `${hiddenChallenges} challenges hidden by filters`,
			subtitle: `Remove ${activeFilters.join(" or ")} filter to see more`,
			cta: "Clear all filters",
		};
	}

	return {
		title: "No challenges found",
		subtitle: "Try removing filters or starting a new search",
		cta: "Reset all",
	};
};

type Challenge = {
	id: number;
	title: string;
	description: string;
	imageUri: string;
	accessType?: "PREMIUM" | "FREE";
	tags: { id: number; label: string }[];
	completed?: boolean;
};

type Section = {
	title: string;
	data: Challenge[];
};

type FlattenedItem = Challenge | { title: string; isHeader: true };

type Props = {
	sections: Section[];
	handleChallengePress?: (id: number) => void;
	handleClearFilters?: () => void;
	isCME?: boolean;
	activeFilters?: {
		searchQuery?: string;
		isPremiumSelected?: boolean;
		isCompletedSelected?: boolean;
	};
	totalChallenges: number;
	isPremiumUser?: boolean;
	contentContainerStyle?: StyleProp<ViewStyle>;
};

const ChallengesView = ({
	sections,
	handleChallengePress,
	handleClearFilters,
	isCME,
	activeFilters,
	totalChallenges,
	isPremiumUser,
	contentContainerStyle,
}: Props) => {
	const renderItem = useCallback(
		({ item }: { item: FlattenedItem }) => {
			if ("isHeader" in item) {
				return <CustomListSubheader>{item.title}</CustomListSubheader>;
			}

			return (
				<ChallengeListItem
					{...item}
					id={Number(item.id)}
					locked={item.accessType === "PREMIUM" && !isPremiumUser}
					onPress={() => handleChallengePress?.(item.id)}
					isCME={isCME}
				/>
			);
		},
		[handleChallengePress, isCME, isPremiumUser],
	);

	const getEmptyState = useCallback(() => {
		const { searchQuery, isPremiumSelected, isCompletedSelected } =
			activeFilters ?? {};
		const hiddenChallenges =
			totalChallenges -
			sections.reduce((acc, section) => acc + section.data.length, 0);
		const activeFilterCount = [isPremiumSelected, isCompletedSelected].filter(
			Boolean,
		).length;

		return getEmptyStateConfig({
			hiddenChallenges,
			activeFilterCount,
			hasSearchQuery: Boolean(searchQuery),
			isPremiumSelected,
			isCompletedSelected,
		});
	}, [activeFilters, sections, totalChallenges]);

	const keyExtractor = useCallback(
		(item: Challenge, index: number) => `${item.id}-${index}`,
		[],
	);

	const renderSectionHeader = useCallback(
		({ section }: { section: Section }) => (
			<CustomListSubheader>{section.title}</CustomListSubheader>
		),
		[],
	);

	const viewabilityConfig = useMemo(
		() => ({
			itemVisiblePercentThreshold: 50,
			minimumViewTime: 1000,
		}),
		[],
	);

	return (
		<SectionList
			sections={sections}
			initialNumToRender={10}
			contentContainerStyle={contentContainerStyle}
			maxToRenderPerBatch={5}
			windowSize={5}
			updateCellsBatchingPeriod={100}
			removeClippedSubviews
			stickySectionHeadersEnabled={false}
			keyExtractor={keyExtractor}
			renderSectionHeader={renderSectionHeader}
			renderItem={renderItem}
			viewabilityConfig={viewabilityConfig}
			getItemLayout={(_, index) => ({
				length: 100,
				offset: 100 * index,
				index,
			})}
			ListEmptyComponent={
				<Box paddingBottom={24}>
					<EmptyState
						title={getEmptyState().title}
						subtitle={getEmptyState().subtitle}
						cta={getEmptyState().cta}
						handleAction={handleClearFilters}
					/>
				</Box>
			}
		/>
	);
};

export default ChallengesView;
